/**
 * Pickup Availability Web Component
 * Manages pickup availability display and drawer functionality
 */
if (!customElements.get('pickup-availability')) {
  customElements.define('pickup-availability', class PickupAvailability extends HTMLElement {
    constructor() {
      super();
      
      // Only initialize if available
      if (!this.hasAttribute('available')) return;

      // Bind methods
      this.onClickRefreshList = this.onClickRefreshList.bind(this);
      this.onClickOpenDrawer = this.onClickOpenDrawer.bind(this);
      this.onClickCloseDrawer = this.onClickCloseDrawer.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      
      // Listen for variant changes
      document.addEventListener('product:variant-change', this.onVariantChange);
      
      // Start fetching availability
      this.fetchAvailability(this.dataset.variantId);
    }

    fetchAvailability(variantId) {
      console.log('📡 Pickup Availability: Fetching data for variant', variantId);
      console.log('🔍 Pickup Availability: Current data-variant-id', this.dataset.variantId);
      
      let rootUrl = this.dataset.rootUrl;
      if (!rootUrl.endsWith("/")) {
        rootUrl = rootUrl + "/";
      }
      const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability&timestamp=${Date.now()}`;

      console.log('🌐 Pickup Availability: Request URL', variantSectionUrl);
      console.log('🎯 Pickup Availability: Expected variant ID in response', variantId);

      fetch(variantSectionUrl, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      })
        .then(response => {
          console.log('📥 Pickup Availability: Response received', response.status);
          return response.text();
        })
        .then(text => {
          console.log('🔄 Pickup Availability: Parsing response and updating UI');
          console.log('📄 Raw response preview:', text.substring(0, 300) + '...');
          
          const sectionInnerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('.shopify-section');
            
          if (sectionInnerHTML) {
            console.log('✅ Pickup Availability: Section parsed successfully');
            
            // Check if we got the correct variant in response
            const responseHtml = sectionInnerHTML.innerHTML;
            const expectedVariantMatch = responseHtml.match(/<!-- Section Variant ID: (\d+) -->/);
            const responseVariantId = expectedVariantMatch ? expectedVariantMatch[1] : 'unknown';
            
            console.log('🔍 Pickup Availability: Response contains variant ID', responseVariantId);
            
            if (responseVariantId !== variantId && responseVariantId !== 'unknown') {
              console.warn('⚠️ Pickup Availability: Variant ID mismatch!');
              console.warn(`   Expected: ${variantId}`);
              console.warn(`   Received: ${responseVariantId}`);
              console.warn('   This may cause incorrect inventory data');
            }
            
            this.renderPreview(sectionInnerHTML);
            console.log('✅ Pickup Availability: UI updated successfully');
          } else {
            console.error('❌ Pickup Availability: Failed to parse .shopify-section from response');
          }
        })
        .catch((error) => {
          console.error('❌ Pickup Availability: Error fetching data', error);
          const button = this.querySelector('button');
          if (button) button.removeEventListener('click', this.onClickRefreshList);
          this.renderError();
        });
    }

    onClickRefreshList() {
      this.fetchAvailability(this.dataset.variantId);
    }

    /**
     * Handle variant change events
     */
    onVariantChange(event) {
      const { variant } = event.detail;
      
      console.log('🔄 Pickup Availability: Variant changed', {
        variantId: variant?.id,
        available: variant?.available,
        productTitle: variant?.product?.title
      });
      
      if (variant && variant.available) {
        console.log('✅ Pickup Availability: Updating data for variant', variant.id);
        
        // Update data attributes
        this.dataset.variantId = variant.id;
        this.setAttribute('available', '');
        
        // Fetch new availability data
        this.fetchAvailability(variant.id);
      } else {
        console.log('❌ Pickup Availability: Variant not available, clearing data');
        
        // Remove availability if variant is not available
        this.removeAttribute('available');
        this.innerHTML = '';
        
        // Clear drawer content as well
        const panel = document.getElementById('Pickup-Availability');
        if (panel) {
          panel.innerHTML = '';
        }
      }
    }

    renderError() {
      this.innerHTML = '';
      const btn = this.querySelector('button');
      if (btn) btn.addEventListener('click', this.onClickRefreshList);
    }

    /**
     * Render the pickup availability preview and drawer
     */
    renderPreview(sectionInnerHTML) {
      console.log('🔄 Pickup Availability: Rendering preview and drawer');
      
      // Set available attribute
      this.setAttribute('available', '');

      // Update the preview content (the visible pickup availability info)
      const previewSection = sectionInnerHTML.querySelector('.pickup-availability-preview');
      if (previewSection) {
        console.log('✅ Pickup Availability: Updating preview content (variant available - Sierra/Warehouse logic applied)');
        this.innerHTML = previewSection.innerHTML;
      } else {
        console.log('❌ Pickup Availability: No preview content (variant sold out)');
        this.innerHTML = '';
      }

      // Update drawer content in the panel
      const panel = document.getElementById('Pickup-Availability');
      const newDrawerContent = sectionInnerHTML.querySelector('.side-availability');
      
      if (panel && newDrawerContent) {
        console.log('✅ Pickup Availability: Completely replacing drawer content');
        console.log('📄 New drawer HTML preview:', newDrawerContent.innerHTML.substring(0, 200) + '...');
        
        // Completely clear and replace panel content
        panel.innerHTML = '';
        
        // Force a reflow to ensure clearing is complete
        panel.offsetHeight;
        
        // Set new content
        panel.innerHTML = newDrawerContent.innerHTML;
        
        // Verify the update
        const updatedContent = panel.querySelector('.pickup-availability-drawer');
        if (updatedContent) {
          console.log('✅ Pickup Availability: Drawer content verification passed');
        } else {
          console.error('❌ Pickup Availability: Drawer content verification failed');
        }
        
        console.log('✅ Pickup Availability: Drawer content updated, setting up functionality');
        
        // Setup drawer functionality
        this.setupDrawer(panel);
      } else {
        console.log('❌ Pickup Availability: Panel or drawer content not found');
        if (!panel) console.log('  - Panel #Pickup-Availability not found');
        if (!newDrawerContent) console.log('  - .side-availability content not found in response');
      }
    }

    /**
     * Setup drawer open/close functionality
     */
    setupDrawer(panel) {
      // Setup open button
      const openBtn = this.querySelector('button');
      if (openBtn) {
        openBtn.addEventListener('click', this.onClickOpenDrawer);
      }

      // Setup close button
      const closeBtn = panel.querySelector('.pickup-availability-drawer-button');
      if (closeBtn) {
        closeBtn.addEventListener('click', this.onClickCloseDrawer);
      }
    }

    /**
     * Handle drawer open
     */
    onClickOpenDrawer(evt) {
      const panel = document.getElementById('Pickup-Availability');
      if (panel) {
        panel.classList.add('active');
        document.body.classList.add('open-cc');
      }
      evt.preventDefault();
    }

    /**
     * Handle drawer close
     */
    onClickCloseDrawer() {
      const panel = document.getElementById('Pickup-Availability');
      if (panel) {
        panel.classList.remove('active');
        document.body.classList.remove('open-cc');
      }
    }

    /**
     * Cleanup when component is removed
     */
    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this.onVariantChange);
    }
  });
}